import { Controller } from "@hotwired/stimulus"

// Cross-page selection for the trade wine lists.
//
// The list re-renders inside a Turbo Frame on every filter, sort and page
// change, which would lose whatever was ticked. This controller wraps the
// frame and the bulk forms, keeps the selection itself (id -> wine id), ticks
// boxes again as they come back (checkboxTargetConnected fires for every
// render) and mirrors the selection into hidden inputs on each bulk form.
//
// A checkbox carries the selection id as its value (the portfolio entry id
// on the portfolio, the wine id on the catalog) and the wine id in
// data-selection-wine-id, so a form that needs wine ids (Create Event) can
// ask for those instead: data-selection-source="wine".
//
// Targets:
//   checkbox  a row's box (inside the frame)
//   all       the select-all box in the table head (inside the frame)
//   count     prints "N selected"
//   label     prints data-selection-template with %{n} and %{s} ("s" unless 1)
//   confirm   a form whose data-turbo-confirm takes the same template
//   bar       hidden while nothing is selected
//   inputs    a holder rebuilt with hidden inputs named data-selection-param
export default class extends Controller {
  static targets = ["checkbox", "all", "count", "label", "confirm", "bar", "inputs"]

  // Target callbacks fire before connect(), so the selection exists from
  // initialize() on.
  initialize() {
    this.selected = new Map()
  }

  connect() {
    this.render()
  }

  checkboxTargetConnected(box) {
    box.checked = this.selected.has(box.value)
    this.paintRow(box)
    this.renderAll()
  }

  allTargetConnected() {
    this.renderAll()
  }

  // The admin wine list keeps its bulk bar inside the frame, so the bar
  // comes back blank on every render; paint it from the selection again.
  barTargetConnected() {
    this.render()
  }

  toggle(event) {
    const box = event.currentTarget
    if (box.checked) {
      this.selected.set(box.value, box.dataset.selectionWineId || box.value)
    } else {
      this.selected.delete(box.value)
    }
    this.render()
  }

  toggleAll(event) {
    const on = event.currentTarget.checked
    this.checkboxTargets.forEach((box) => {
      box.checked = on
      if (on) {
        this.selected.set(box.value, box.dataset.selectionWineId || box.value)
      } else {
        this.selected.delete(box.value)
      }
    })
    this.render()
  }

  clear() {
    this.selected.clear()
    this.checkboxTargets.forEach((box) => { box.checked = false })
    this.render()
  }

  render() {
    const n = this.selected.size
    const fill = (template) => template.replace("%{n}", String(n)).replace("%{s}", n === 1 ? "" : "s")
    this.countTargets.forEach((el) => { el.textContent = `${n} selected` })
    this.labelTargets.forEach((el) => { el.textContent = fill(el.dataset.selectionTemplate || "%{n}") })
    this.confirmTargets.forEach((el) => { el.dataset.turboConfirm = fill(el.dataset.selectionTemplate || "") })
    this.barTargets.forEach((el) => { el.hidden = n === 0 })
    this.element.classList.toggle("has-selection", n > 0)
    this.inputsTargets.forEach((holder) => this.fillInputs(holder))
    this.checkboxTargets.forEach((box) => this.paintRow(box))
    this.renderAll()
  }

  fillInputs(holder) {
    const name = holder.dataset.selectionParam
    const wantWine = holder.dataset.selectionSource === "wine"
    holder.replaceChildren()
    for (const [id, wineId] of this.selected) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = wantWine ? wineId : id
      holder.appendChild(input)
    }
  }

  paintRow(box) {
    const row = box.closest("tr")
    if (row) row.classList.toggle("selected", box.checked)
  }

  renderAll() {
    if (!this.hasAllTarget) return
    const boxes = this.checkboxTargets
    const checked = boxes.filter((box) => box.checked).length
    this.allTarget.checked = boxes.length > 0 && checked === boxes.length
    this.allTarget.indeterminate = checked > 0 && checked < boxes.length
  }
}
